<template>
    <Transition :name="dropdownDirection">
        <div
            ref="dropdown"
            v-if="visible"
            class="selection-dropdown"
            :style="dropdownPosition"
        >
            <!-- Search input wrapper -->
            <div
                class="d-flex justify-content-between align-items-center form-group"
            >
                <div
                    ref="searchInputWrapper"
                    class="form-control search-input-wrapper"
                >
                    <div class="search-left">
                        <template
                            v-for="(key, index) in selectionOrder"
                            :key="index"
                        >
                            <SelectedBadge
                                v-if="isValidOption(key)"
                                :text="getItemDisplayText(findOptionByKey(key))"
                                :is-search-badge="true"
                                :removable="true"
                                @remove="handleRemoveSelection(key)"
                            />
                        </template>
                        <input
                            ref="searchInput"
                            type="text"
                            class="search-input"
                            v-model="searchQuery"
                            @keydown="handleKey"
                            @keydown.backspace="handleBackspace"
                            :placeholder="searchPlaceholder"
                        />
                    </div>
                    <div v-if="selectedCount" class="search-right">
                        <i
                            class="fi fi-x clear-search"
                            @click.stop="handleClearSelection"
                            title="Clear all"
                        ></i>
                    </div>
                </div>
            </div>

            <!-- Dropdown content Options-->
            <div ref="dropdownContent" class="dropdown-content">
                <div class="virtual-scroll-container">
                    <div
                        v-for="(option, index) in displayOptions"
                        :key="getOptionKey(option, index)"
                        class="option-item"
                        :class="{
                            focused: index === focusedIndex && !isCreating,
                            selected: isOptionSelected(option),
                        }"
                        @click="toggleOption(option, $event)"
                    >
                        <div
                            class="form-check d-flex align-items-center"
                            style="gap: 0.4375rem"
                        >
                            <input
                                class="form-check-input"
                                type="checkbox"
                                :value="getOptionValue(option)"
                                :id="`option-${index}`"
                                :checked="isOptionSelected(option)"
                                @click.stop="toggleOption(option, $event)"
                            />
                            <label
                                :for="`option-${index}`"
                                class="checkbox-label text-muted"
                                style="cursor: pointer"
                            >
                                {{ getItemDisplayText(option) }}
                            </label>
                            <i
                                v-if="allowCreate"
                                class="fi fi-x fi-sm ms-auto close-icon"
                                @click.stop="handleRemoveOption(option)"
                            ></i>
                        </div>
                    </div>
                    <!-- Add "Create New" Option -->
                    <div
                        v-if="showCreateOption"
                        class="option-item"
                        :class="{ focused: isCreating }"
                        @click.stop="handleCreateOption"
                    >
                        <div
                            class="form-check d-flex flex-column align-items-center"
                            style="gap: 0.4375rem"
                        >
                            <span
                                class="d-flex align-items-center checkbox-label w-100 text-muted"
                                style="gap: 0.4375rem"
                            >
                                Create
                                <SelectedBadge
                                    :style="{ maxHeight: '1.25rem' }"
                                    :text="searchQuery"
                                />
                            </span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </Transition>
</template>

<script>
import SelectedBadge from './SelectedBadge.vue';

/**
 * @component MultiselectDropdown
 * @description Dropdown component for selecting multiple options with search functionality
 */
export default {
    name: 'MultiselectDropdown',
    components: {
        SelectedBadge,
    },
    props: {
        /** Controls dropdown visibility */
        visible: {
            type: Boolean,
            default: false,
        },
        /** Position styling for dropdown placement */
        dropdownPosition: {
            type: Object,
            default: () => ({
                top: '0px',
                left: '0px',
                width: '0px',
                maxHeight: '400px',
            }),
        },
        /** Direction for dropdown animation */
        dropdownDirection: {
            type: String,
            default: 'dropdown-stretch-down',
        },
        /** Array of options to display */
        options: {
            type: Array,
            required: true,
        },
        /** Selected values object */
        modelValue: {
            type: Object,
            default: () => ({}),
        },
        /** Type of selection behavior */
        selectionType: {
            type: String,
            default: 'multiselect',
            validator: (value) =>
                ['multiselect', 'single-select'].includes(value),
        },
        /** Placeholder text for search input */
        searchPlaceholder: {
            type: String,
            default: 'Search...',
        },
        /** Array of selected item objects */
        selectedItems: {
            type: Array,
            default: () => [],
        },
        /** Array tracking selection order */
        selectionOrder: {
            type: Array,
            default: () => [],
        },
        /** Count of selected items */
        selectedCount: {
            type: Number,
            default: 0,
        },
        /** Whether selection is required */
        required: {
            type: Boolean,
            default: false,
        },
        /** Allow creation of new options */
        allowCreate: {
            type: Boolean,
            default: false,
        },
    },
    emits: [
        'update:model-value',
        'remove-option',
        'clear-selection',
        'close-dropdown',
        'validate',
        'create-option',
    ],
    data() {
        return {
            searchQuery: '', // Search query text
            focusedIndex: 0, // Index of currently focused option
            selectedValues: { ...this.modelValue }, // Local copy of selected values
            internalSelectionOrder: [...this.selectionOrder], // Track selection order
            itemHeight: 1.75, // Height of each option item
            debounceTimer: null, // Timer for debouncing search
            initialOptions: [], // Initial subset of options
            isFullyRendered: false, // Whether all options are rendered
            defaultsInitialized: false, // Track if defaults initialized
        };
    },
    computed: {
        /** Filtered options based on search query */
        filteredOptions() {
            if (!this.searchQuery) return this.options;

            const query = this.searchQuery.toLowerCase();
            return this.options.filter((option) => {
                const text = this.getItemDisplayText(option).toLowerCase();
                return text.includes(query);
            });
        },

        /** Options to display in dropdown */
        displayOptions() {
            return this.isFullyRendered
                ? this.filteredOptions
                : this.initialOptions;
        },

        /** Whether to show the create option */
        showCreateOption() {
            return this.allowCreate && this.searchQuery;
        },

        /** Whether the create option is currently focused */
        isCreating() {
            return (
                this.showCreateOption &&
                this.focusedIndex === this.displayOptions.length
            );
        },
    },
    watch: {
        /** Updates local selected values when model changes */
        modelValue: {
            deep: true,
            handler(newVal) {
                this.selectedValues = { ...newVal };

                if (!this.defaultsInitialized) {
                    this.initializeDefaultValues();
                }

                this.updateSelectionOrderFromModel();
            },
        },
        /** Handles search query changes */
        searchQuery() {
            this.handleSearchQueryChange();
        },
        /** Handles dropdown visibility changes */
        visible(newVal) {
            if (newVal) {
                this.handleDropdownOpen();
            } else {
                this.searchQuery = '';
                this.focusedIndex = 0;
            }
        },
        /** Validates field when selected values change */
        selectedValues: {
            deep: true,
            handler(newVal) {
                this.$emit('validate', this.checkValidity());

                const hasSelections = Object.values(newVal).some(
                    (value) => value === true
                );
                if (!hasSelections) {
                    this.internalSelectionOrder = [];
                }
            },
        },
        /** Clears selection order when count becomes zero */
        selectedCount(newVal) {
            if (newVal === 0) {
                this.internalSelectionOrder = [];
            }
        },

        selectionOrder: {
            immediate: true,
            handler(newOrder) {
                this.internalSelectionOrder = [...newOrder];
            },
        },
    },
    mounted() {
        this.initializeDefaultValues();

        Object.keys(this.modelValue).forEach((key) => {
            if (
                (this.modelValue[key] || this.isDefaultOption(key)) &&
                !this.internalSelectionOrder.includes(key)
            ) {
                this.internalSelectionOrder.push(key);
            }
        });

        this.calculateItemHeight();
    },
    beforeUnmount() {
        clearTimeout(this.debounceTimer);
    },
    methods: {
        /** Generates unique key for option */
        getOptionKey(option) {
            // Use crypto.randomUUID() to generate a unique identifier
            try {
                return crypto.randomUUID();
            } catch (e) {
                // Fallback for browsers that don't support randomUUID
                const value = this.getOptionValue(option);
                return (
                    btoa(JSON.stringify(value)) +
                    '-' +
                    Math.random().toString(36).substring(2)
                );
            }
        },

        /** Extracts value from option */
        getOptionValue(option) {
            return option?.value || option;
        },

        /** Gets display text for option */
        getItemDisplayText(item) {
            return item?.text || item?.label || item;
        },

        /** Checks if option is selected */
        isOptionSelected(option) {
            const key = this.getOptionValue(option);
            return !!this.selectedValues[key];
        },

        /** Toggles selection state of option */
        toggleOption(option, event) {
            if (event.target.tagName.toLowerCase() !== 'input') {
                event.preventDefault();
                event.stopPropagation();
            }

            const key = this.getOptionValue(option);

            if (this.selectionType === 'single-select') {
                const wasSelected = !!this.selectedValues[key];

                Object.keys(this.selectedValues).forEach((k) => {
                    this.selectedValues[k] = false;
                });

                const newOrder = [];

                if (!wasSelected) {
                    this.selectedValues[key] = true;
                    newOrder.push(key);
                }

                this.$emit(
                    'update:model-value',
                    { ...this.selectedValues },
                    newOrder
                );

                this.searchQuery = '';
                this.$emit('close-dropdown');
            } else {
                const wasSelected = !!this.selectedValues[key];

                this.selectedValues[key] = !wasSelected;

                let newOrder = [...this.internalSelectionOrder];
                if (this.selectedValues[key] && !wasSelected) {
                    newOrder.push(key);
                } else if (!this.selectedValues[key] && wasSelected) {
                    newOrder = newOrder.filter((k) => k !== key);
                }

                this.$emit(
                    'update:model-value',
                    { ...this.selectedValues },
                    newOrder
                );

                this.searchQuery = '';

                const optionIndex = this.displayOptions.findIndex(
                    (opt) => this.getOptionValue(opt) == key
                );

                if (optionIndex !== -1) {
                    this.focusedIndex = optionIndex;

                    if (this.selectedValues[key]) {
                        this.$nextTick(() => {
                            this.scrollToFocusedOption();
                        });
                    }
                }

                this.$nextTick(() => {
                    if (this.$refs.searchInput) {
                        this.$refs.searchInput.focus({ preventScroll: true });
                    }
                });
            }

            this.validateField();
        },

        /** Validates field and emits validation event */
        validateField() {
            this.$emit('validate', this.checkValidity());
        },

        /** Handles backspace key to remove last selected item */
        handleBackspace(event) {
            if (
                this.searchQuery ||
                !this.selectedItems.length ||
                event.keyCode !== 8
            ) {
                return;
            }

            const lastSelectedKey =
                this.selectionOrder[this.selectionOrder.length - 1];
            if (lastSelectedKey) {
                this.$emit('remove-selection', lastSelectedKey);
            }
        },

        /** Handles clear selection action */
        handleClearSelection() {
            this.$emit('clear-selection');

            this.$nextTick(() => {
                if (this.$refs.searchInput) {
                    this.$refs.searchInput.focus({ preventScroll: true });
                }
            });
        },

        /** Handles remove selection action */
        handleRemoveSelection(key) {
            this.$emit('remove-selection', key);

            this.$nextTick(() => {
                if (this.$refs.searchInput) {
                    this.$refs.searchInput.focus({ preventScroll: true });
                }
            });
        },

        /** Handles Enter key press */
        handleEnterPress() {
            if (this.isCreating) {
                this.handleCreateOption();
                return;
            }

            if (this.displayOptions.length === 0) return;

            const option = this.displayOptions[this.focusedIndex];
            if (!option) return;

            const key = this.getOptionValue(option);
            const wasSelected = !!this.selectedValues[key];

            if (this.selectionType === 'single-select') {
                Object.keys(this.selectedValues).forEach((k) => {
                    this.selectedValues[k] = false;
                });

                const newOrder = [];

                if (!wasSelected) {
                    this.selectedValues[key] = true;
                    newOrder.push(key);
                }

                this.searchQuery = '';
                this.$emit('close-dropdown');
                this.$emit(
                    'update:model-value',
                    { ...this.selectedValues },
                    newOrder
                );
            } else {
                this.selectedValues[key] = !this.selectedValues[key];

                let newOrder = [...this.internalSelectionOrder];
                if (this.selectedValues[key] && !wasSelected) {
                    newOrder.push(key);
                } else if (!this.selectedValues[key] && wasSelected) {
                    newOrder = newOrder.filter((k) => k !== key);
                }

                this.searchQuery = '';
                this.$emit(
                    'update:model-value',
                    { ...this.selectedValues },
                    newOrder
                );

                this.$nextTick(() => {
                    this.scrollToFocusedOption();
                });
            }

            this.$nextTick(() => {
                if (this.$refs.searchInput) {
                    this.$refs.searchInput.focus({ preventScroll: true });
                }
            });

            this.validateField();
        },

        /** Handles keyboard navigation */
        handleKey(event) {
            if (
                ['ArrowDown', 'ArrowUp', 'Enter', 'Escape'].includes(event.key)
            ) {
                event.preventDefault();

                if (event.key === 'ArrowDown') {
                    this.focusNextOption();
                } else if (event.key === 'ArrowUp') {
                    this.focusPreviousOption();
                } else if (event.key === 'Enter') {
                    this.handleEnterPress();
                } else if (event.key === 'Escape') {
                    this.$emit('close-dropdown');
                }
            }
        },

        /** Focuses next option in list */
        focusNextOption() {
            const totalOptions =
                this.displayOptions.length + (this.showCreateOption ? 1 : 0);
            if (totalOptions === 0) return;

            this.focusedIndex = (this.focusedIndex + 1) % totalOptions;
            this.scrollToFocusedOption();
        },

        /** Focuses previous option in list */
        focusPreviousOption() {
            const totalOptions =
                this.displayOptions.length + (this.showCreateOption ? 1 : 0);
            if (totalOptions === 0) return;

            this.focusedIndex =
                (this.focusedIndex - 1 + totalOptions) % totalOptions;
            this.scrollToFocusedOption();
        },

        /** Scrolls dropdown to show focused option */
        scrollToFocusedOption() {
            this.$nextTick(() => {
                const scrollContainer =
                    this.$refs.dropdownContent?.querySelector(
                        '.virtual-scroll-container'
                    );
                const allItems =
                    this.$refs.dropdownContent?.querySelectorAll(
                        '.option-item'
                    );

                if (
                    allItems &&
                    allItems[this.focusedIndex] &&
                    scrollContainer
                ) {
                    const itemTop = allItems[this.focusedIndex].offsetTop;
                    const containerHeight = scrollContainer.clientHeight;
                    const itemHeight = allItems[this.focusedIndex].offsetHeight;

                    const currentScrollTop = scrollContainer.scrollTop;
                    const visibleTop = currentScrollTop;
                    const visibleBottom = currentScrollTop + containerHeight;
                    const itemBottom = itemTop + itemHeight;

                    if (itemTop < visibleTop || itemBottom > visibleBottom) {
                        let scrollPosition;

                        if (itemTop < visibleTop) {
                            scrollPosition = itemTop - itemHeight;
                        } else if (itemBottom > visibleBottom) {
                            scrollPosition =
                                itemBottom - containerHeight + itemHeight;
                        } else {
                            return;
                        }

                        scrollContainer.scrollTo({
                            top: Math.max(0, scrollPosition),
                            behavior: 'smooth',
                        });
                    }
                }
            });
        },

        /** Handles search query changes with debouncing */
        handleSearchQueryChange() {
            this.isFullyRendered = false;

            clearTimeout(this.debounceTimer);
            this.debounceTimer = setTimeout(() => {
                this.focusedIndex = 0;
                this.initialOptions = this.filteredOptions.slice(0, 30);
                this.deferFullRendering();
            }, 200);
        },

        /** Focuses search input */
        focusSearchInput() {
            this.$nextTick(() => {
                if (!this.$refs.searchInput || typeof window === 'undefined')
                    return;

                const nav = window?.navigator || {};
                // const platform = nav?.platform || '';
                // const userAgent = nav?.userAgent || '';
                // const isIOS =
                //     /iPad|iPhone|iPod/.test(platform) ||
                //     (platform === 'MacIntel' && nav?.maxTouchPoints > 1);

                // if (isIOS) {
                //     const match = userAgent.match(/OS (\d+)_(\d+)_?(\d+)?/);
                //     const majorVersion = match ? parseInt(match[1], 10) : 0;
                //     // iOS 26 have a bug where the scrolling is not working properly due to auto focus.
                //     // temp disable auto focus for iOS 26 and above.
                //     if (majorVersion >= 26) {
                //         return;
                //     }
                // }
                
                // disable for all touch devices
                const isTouchDevice =
                    (nav.maxTouchPoints || 0) > 0 || 'ontouchstart' in window;

                if (isTouchDevice) {
                    return;
                }

                this.$refs.searchInput.focus({ preventScroll: true });
            });
        },

        /** Defers full rendering of options for performance */
        deferFullRendering() {
            this.$nextTick(() => {
                this.scrollToFocusedOption();

                setTimeout(() => {
                    this.isFullyRendered = true;
                    this.$nextTick(() => {
                        this.scrollToFocusedOption();
                    });
                }, 50);
            });
        },

        /** Handles dropdown opening */
        handleDropdownOpen() {
            this.searchQuery = '';

            if (!this.defaultsInitialized) {
                this.initializeDefaultValues();
            }

            this.updateSelectionOrderFromModel();

            this.initialOptions = this.options.slice(0, 30);

            this.isFullyRendered = false;
            this.focusedIndex = 0;
            this.deferFullRendering();
            this.focusSearchInput();
        },

        /** Calculates item height for scroll calculations */
        calculateItemHeight() {
            this.$nextTick(() => {
                if (this.$refs.dropdownContent) {
                    const options =
                        this.$refs.dropdownContent.querySelectorAll(
                            '.option-item'
                        );
                    if (options && options.length > 0) {
                        this.itemHeight = options[0].offsetHeight || 36;
                    }
                }
            });
        },

        /** Checks if field is valid */
        checkValidity() {
            if (this.required) {
                return Object.values(this.selectedValues).some(
                    (value) => value === true
                );
            }

            return true;
        },

        /** Updates selection order from model */
        updateSelectionOrderFromModel() {
            const newOrder = [];

            this.internalSelectionOrder.forEach((key) => {
                if (this.selectedValues[key] || this.isDefaultOption(key)) {
                    newOrder.push(key);
                }
            });

            Object.keys(this.selectedValues).forEach((key) => {
                if (
                    (this.selectedValues[key] || this.isDefaultOption(key)) &&
                    !newOrder.includes(key)
                ) {
                    newOrder.push(key);
                }
            });

            this.internalSelectionOrder = newOrder;
        },

        /** Checks if option is a default option */
        isDefaultOption(key) {
            const option = this.options.find(
                (opt) => this.getOptionValue(opt) == key
            );
            return option?.default || false;
        },

        /** Initializes default values from options */
        initializeDefaultValues() {
            let hasChanges = false;

            this.options.forEach((opt) => {
                const value = this.getOptionValue(opt);
                if (opt.default && !this.selectedValues[value]) {
                    this.selectedValues[value] = true;
                    if (!this.internalSelectionOrder.includes(value)) {
                        this.internalSelectionOrder.push(value);
                    }
                    hasChanges = true;
                }
            });

            this.defaultsInitialized = true;

            if (hasChanges) {
                this.$emit(
                    'update:model-value',
                    { ...this.selectedValues },
                    this.internalSelectionOrder
                );
            }
        },

        /** Checks if a key corresponds to a valid option */
        isValidOption(key) {
            const option = this.options?.find(
                (opt) => this.getOptionValue(opt) == key
            );
            if (option) return true;

            if (this.selectedItems?.length) {
                const selectedItem = this.selectedItems.find(
                    (item) => this.getOptionValue(item) == key
                );
                if (selectedItem) return true;
            }

            return false;
        },

        /** Finds option object by key/value */
        findOptionByKey(key) {
            const option = this.options.find(
                (opt) => this.getOptionValue(opt) == key
            );
            if (option) return option;

            if (this.selectedItems && this.selectedItems.length) {
                const selectedItem = this.selectedItems.find(
                    (item) => this.getOptionValue(item) == key
                );
                if (selectedItem) return selectedItem;
            }

            return key;
        },

        /** Handles clicking the create option */
        handleCreateOption() {
            if (!this.showCreateOption) return;

            const newOptionValue = this.searchQuery;
            this.$emit('create-option', newOptionValue);

            this.searchQuery = '';
            this.focusSearchInput();
        },

        /** Focuses selected option in full options list */
        focusSelectedOptionInOptions() {
            this.focusedIndex = 0;
        },

        /** Handles option removal for form template builder */
        handleRemoveOption(optionToRemove) {
            this.$emit('remove-option', optionToRemove);
            const value = this.getOptionValue(optionToRemove);
            this.handleRemoveSelection(value);
        },
    },
};
</script>

<style scoped>
.selection-dropdown {
    position: fixed;
    background: var(--white);
    border: 1px solid var(--neutral-600);
    border-radius: var(--radius-default);
    z-index: 1072;
    display: flex;
    flex-direction: column;
    pointer-events: auto;
    padding: var(--space-3);
    gap: var(--space-2);
}

.selection-dropdown:hover,
.selection-dropdown:focus-within {
    border-color: var(--primary-200);
}

.dropdown-content {
    display: flex;
    flex-direction: column;
    padding-top: 0;
    opacity: 1;
    overflow: hidden;
}

.search-input-wrapper {
    display: flex;
    width: 100%;
    gap: var(--space-1);
    min-height: 2.5rem;
    padding: 0.25rem;
    border-radius: var(--radius-default);
    transition: border-color 0.2s ease;
    max-height: 100%;
}

.search-left {
    width: 92%;
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: var(--space-1);
}

.search-right {
    width: 8%;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: var(--space-1) var(--space-2);
    height: 1.75rem;
    width: 1.75rem;
    border-radius: 50%;
    background-color: var(--neutral-400);
    color: var(--neutral-800);
    transition: background-color 0.2s ease;
}

.clear-search {
    font-size: 0.875rem;
    cursor: pointer;
    line-height: 1;
    opacity: 0.7;
    transition: all 0.2s ease;
}

.clear-search:hover {
    color: var(--neutral-900);
    opacity: 1;
    transform: scale(1.1);
}

.search-input {
    display: inline-flex;
    height: 1.5rem;
    font-size: var(--size-3);
    background: transparent;
    border: none;
    outline: none;
    min-width: 1.875rem;
    width: 1.875rem;
    margin: 0;
    margin-left: var(--space-1);
    padding: 0;
    flex: 1;
}

.form-check {
    min-height: var(--space-5);
}

.checkbox-label {
    font-size: 0.875rem;
    line-height: 1.25;
}

.virtual-scroll-container {
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    scroll-behavior: smooth;
    overscroll-behavior: contain;
    -webkit-overflow-scrolling: touch;
}

.option-item {
    padding: var(--space-1) var(--space-2);
    cursor: pointer;
    border-radius: var(--radius-sm);
    transition: background-color 0.2s ease;
}

.option-item:hover {
    background-color: var(--neutral-450);
}

.option-item.focused {
    background-color: var(--neutral-600);
}

.option-item:focus {
    outline: none;
}

.close-icon {
    color: var(--neutral-800);
    transition: transform 0.15s ease, color 0.15s ease;
}

.close-icon:hover {
    color: var(--neutral-900);
    /* slightly darker for emphasis */
}

/* transition for dropdown */
.dropdown-stretch-down-enter-active,
.dropdown-stretch-down-leave-active {
    transition: transform 0.3s cubic-bezier(0.25, 0.8, 0.25, 1),
        opacity 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
    transform-origin: top left;
}

.dropdown-stretch-down-enter-from,
.dropdown-stretch-down-leave-to {
    opacity: 0;
    transform: scaleY(0);
}

.dropdown-stretch-down-enter-to,
.dropdown-stretch-down-leave-from {
    opacity: 1;
    transform: scaleY(1);
}

.dropdown-stretch-up-enter-active,
.dropdown-stretch-up-leave-active {
    transition: transform 0.3s cubic-bezier(0.25, 0.8, 0.25, 1),
        opacity 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
    transform-origin: bottom left;
}

.dropdown-stretch-up-enter-from,
.dropdown-stretch-up-leave-to {
    opacity: 0;
    transform: scaleY(0);
}

.dropdown-stretch-up-enter-to,
.dropdown-stretch-up-leave-from {
    opacity: 1;
    transform: scaleY(1);
}
</style>
